import { useSelector } from "@xstate/store-react";
import { floatingPromptStore } from "@/app/command-palette/floating-prompt-store";
import { panelStore } from "@/app/providers/panel-store";
import { type AgentMode, agentUiStore } from "./agent-ui-store";
import { isFullPageChatActive } from "./full-page-chat-store";

export const useAgentMode = () => useSelector(agentUiStore, (s) => s.context.mode);

/** Is the agent surface currently visible (whichever mode is active)? */
export function isAgentOpen(): boolean {
  const mode = agentUiStore.get().context.mode;
  if (mode === "panel") return panelStore.get().context.activePanel === "agent";
  return floatingPromptStore.get().context.open;
}

/**
 * Open the agent in the active mode. Ensures the other surface is closed first
 * (exclusivity).
 *
 * No-op while the full-page `/chat` route owns the screen: that route *is* the
 * agent, so a gutter panel or floating prompt beside it would render the same
 * conversation twice. Guarding here rather than at each call site covers the
 * hotkey, the palette command, and any future affordance in one place.
 */
export function openAgent(): void {
  if (isFullPageChatActive()) return;
  const mode = agentUiStore.get().context.mode;
  if (mode === "panel") {
    floatingPromptStore.send({ type: "prompt.close" });
    panelStore.send({ type: "panel.open", panel: "agent" });
  } else {
    panelStore.send({ type: "panel.close" });
    floatingPromptStore.send({ type: "prompt.open", taskId: null });
  }
}

export function closeAgent(): void {
  const mode = agentUiStore.get().context.mode;
  if (mode === "panel") panelStore.send({ type: "panel.close" });
  else floatingPromptStore.send({ type: "prompt.close" });
}

export function toggleAgent(): void {
  if (isAgentOpen()) closeAgent();
  else openAgent();
}

/**
 * Flip between panel ↔ floating. No-op when neither surface is open — swapping a
 * hidden surface has no visible effect, so we reserve this shortcut for live swaps.
 */
export function switchAgentMode(): void {
  if (!isAgentOpen()) return;
  closeAgent();
  const next: AgentMode = agentUiStore.get().context.mode === "panel" ? "floating" : "panel";
  agentUiStore.send({ type: "agent.setMode", mode: next });
  openAgent();
}
